import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { Dropdown, DropdownItem, DropdownMenu, DropdownToggle } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBan } from '@fortawesome/pro-light-svg-icons/faBan';
import clsx from 'clsx';
import { TypedMemo } from '../../../common/types';
import type { PeopleRow } from '../outreachTypes';
import { peopleMessages as messages } from './peopleMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the campaign page; each part uses its own slice of it */
import styles from './Campaign.module.scss';
/* eslint-enable css-modules/no-unused-class */

export interface BlockMenuActions {
  blockPerson: (row: PeopleRow, name: string) => void;
  blockTarget: (row: PeopleRow) => void;
  fewer: (row: PeopleRow) => void;
  stopFollowUp: (messageUuid: string) => void;
}

interface BlockMenuProps {
  row: PeopleRow;
  actions: BlockMenuActions;
  disabled: boolean;
}

/**
 * The block menu of a pitch (design outreach-v2 g2, t4): never pitch the person, never pitch the
 * playlist, fewer like this, stop the follow-up. It sits in the footer of the details panel as
 * "Never pitch again". "Fewer like this" needs a sent pitch.
 */
const BlockMenuComponent: React.FC<BlockMenuProps> = ({ row, actions, disabled }) => {
  const intl = useIntl();
  const [isOpen, setIsOpen] = useState(false);
  const name = row.name ?? '';
  return (
    <Dropdown isOpen={isOpen} toggle={() => setIsOpen((open) => !open)} direction="up">
      <DropdownToggle
        color="link"
        size="sm"
        className={styles.menuDanger}
        disabled={disabled}
        aria-label={intl.formatMessage(messages.blockMenu, { name })}
        data-testid="people-block-menu"
      >
        <FontAwesomeIcon icon={faBan} className="me-2" />
        {intl.formatMessage(messages.neverPitchAgain)}
      </DropdownToggle>
      <DropdownMenu end>
        {row.workspaceContactUuid !== null && (
          <DropdownItem onClick={() => actions.blockPerson(row, name)}>
            <span className={styles.menuItem}>
              <span>{intl.formatMessage(messages.blockPerson)}</span>
              <small>{intl.formatMessage(messages.blockPersonSub)}</small>
            </span>
          </DropdownItem>
        )}
        {row.targetUuid !== null && (
          <DropdownItem onClick={() => actions.blockTarget(row)}>
            <span className={styles.menuItem}>
              <span>{intl.formatMessage(messages.blockTarget, { name })}</span>
              <small>{intl.formatMessage(messages.blockTargetSub)}</small>
            </span>
          </DropdownItem>
        )}
        {row.targetUuid !== null && row.result !== 'scheduled' && (
          <DropdownItem onClick={() => actions.fewer(row)}>
            <span className={styles.menuItem}>
              <span>{intl.formatMessage(messages.fewer)}</span>
              <small>{intl.formatMessage(messages.fewerSub)}</small>
            </span>
          </DropdownItem>
        )}
        {row.followUpAt !== null && (
          <DropdownItem onClick={() => actions.stopFollowUp(row.messageUuid)}>
            <span className={clsx(styles.menuItem, styles.menuDanger)}>
              <span>{intl.formatMessage(messages.stopFollowUp)}</span>
              <small>{intl.formatMessage(messages.stopFollowUpSub)}</small>
            </span>
          </DropdownItem>
        )}
      </DropdownMenu>
    </Dropdown>
  );
};

export const BlockMenu = TypedMemo(BlockMenuComponent);
